import { createFileRoute } from "@tanstack/react-router";
import type { FermentationGroupId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { CalibrationPanel } from "@/features/brewcontrol/calibration/calibration-panel";
import { NodeEventsStream } from "@/features/brewcontrol/use-node-events-stream";
import { TankBadge } from "@/features/fermentation-journal/components/tank-chrome";

export const Route = createFileRoute(
  "/fermentations/$group_id/setup/calibration",
)({
  component: SetupCalibration,
});

/**
 * Probe calibration per tank, before inoculation. One panel per tank with a
 * vessel, each on its own node; the node streams are mounted once per
 * distinct node so a rig's four tanks share one subscription.
 */
function SetupCalibration() {
  const { group_id } = Route.useParams();
  const groupId = group_id as unknown as FermentationGroupId;
  const { data: group, isLoading } =
    trpc.fermentations.groups.getWithAllDetails.useQuery(groupId);
  const fermentations = (group?.fermentations ?? []).filter(
    (f) => f.equipment_id != null,
  );
  const targets = trpc.useQueries((t) =>
    fermentations.map((f) =>
      t.fermentationControl.getTarget({ fermentationId: f.id }),
    ),
  );

  if (isLoading || !group)
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );

  const nodeIds = [
    ...new Set(
      targets
        .map((q) => q.data?.nodeId)
        .filter((id) => id !== undefined && id !== null),
    ),
  ];

  return (
    <div className="space-y-6 p-4">
      {nodeIds.map((nodeId) => (
        <NodeEventsStream key={nodeId} nodeId={nodeId} />
      ))}
      {fermentations.length === 0 && (
        <p className="text-sm text-olive-11">
          No tank in this group has a vessel assigned.
        </p>
      )}
      {fermentations.map((f, i) => {
        const target = targets[i];
        const equipmentId = f.equipment_id;
        if (equipmentId === null) return null;
        if (target?.isLoading)
          return <Skeleton key={f.id} className="h-16 w-full" />;
        if (!target?.data)
          return (
            <section
              key={f.id}
              className="flex items-center gap-3 border-b border-olive-6 pb-1.5"
            >
              <TankBadge fermentation={f} />
              <span className="text-sm text-olive-11">
                This tank's vessel is not linked to a BrewControl workspace.
              </span>
            </section>
          );
        return (
          <CalibrationPanel
            key={f.id}
            equipmentId={equipmentId}
            fermentationId={f.id}
            header={<TankBadge fermentation={f} />}
          />
        );
      })}
    </div>
  );
}
